import { GetXsrfToken, linkEGate, XSRF_TOKEN_NAME } from "../../helpers/eGateApi";
import { authorizeFinalizeTotpV2, authorizeSetupTotpV2, unwrap, usersResetTotpSettings } from "../../api";
import { AuthorizeWebResponse } from "../../api/types";

export type TotpInitSetupResult =
  | "success"
  | "unauthorized"
  | "web_login_only"
  | "already_set"
  | "no_internal_credentials"
  | "no_cockpit_access";

export type TotpFinalizeSetupResult =
  | "success"
  | "unauthorized"
  | "web_login_only"
  | "not_started"
  | "invalid";

export interface TotpInitSetupResultData {
  result: TotpInitSetupResult;
  url: string | null;
}

export interface TotpFinalizeSetupResultData {
  result: TotpFinalizeSetupResult;
  backupCodes: string[] | null;
}

export function initTotpSetup(): Promise<TotpInitSetupResultData> {
  return unwrap(authorizeSetupTotpV2()) as Promise<TotpInitSetupResultData>;
}

export function finalizeTotpSetup(code: string): Promise<TotpFinalizeSetupResultData> {
  return unwrap(authorizeFinalizeTotpV2({ body: { code } })) as Promise<TotpFinalizeSetupResultData>;
}

// Clears TOTP + backup codes for the current user.
// Returns 204 No Content on success; backend ignores the body but the endpoint
// is declared with a PasswordResetRequestCreateDto, so we pass {} to satisfy
// the model binder.
//
// Resolves on success and rejects on failure — it no longer hands back a Response.
// The previous `Promise<Response>` let callers test `response.ok`, but fetchEGate
// already rejected on a non-ok status, so that branch was unreachable.
export function resetTotp() {
  return unwrap(usersResetTotpSettings({ body: {} as never }));
}

// Uses raw fetch (not fetchEGate) so we can read AuthorizeWebResponse from
// both 200 (success) and 401 (invalid/suspended) without the global error
// notification fetchEGate would emit.
async function postAuthorizeCode(path: string, code: string): Promise<{
  ok: boolean;
  data: AuthorizeWebResponse;
}> {
  const url = linkEGate(path, undefined, "v2");
  const response = await fetch(url, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      [XSRF_TOKEN_NAME]: GetXsrfToken(),
    },
    body: JSON.stringify({ code }),
  });
  const data: AuthorizeWebResponse = await response.json();
  return { ok: response.ok, data };
}

export function verifyLoginTotp(code: string) {
  return postAuthorizeCode("authorize/login/totp", code);
}

export function verifyLoginBackupCode(code: string) {
  return postAuthorizeCode("authorize/login/totp-backup", code);
}
